import React, { useState, useEffect } from 'react';
import { Phone, Copy, CheckCircle, HelpCircle, XCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PHONE_TYPES = [
  { key: 'android', label: 'Android' },
  { key: 'iphone', label: 'iPhone' },
  { key: 'landline', label: 'Landline' },
];

const FORWARDING_CODES = {
  android: {
    all:        '*21*{NUMBER}#',
    unanswered: '*61*{NUMBER}*11*10#',
    busy:       '*67*{NUMBER}#',
  },
  iphone: {
    all:        '*21*{NUMBER}#',
    unanswered: '*61*{NUMBER}*11*10#',
    busy:       '*67*{NUMBER}#',
  },
  landline: {
    all:        '*21*{NUMBER}#',
    unanswered: '*61*{NUMBER}#',
    busy:       '*67*{NUMBER}#',
  },
};

export default function StepNumber({ data, updateData, onNext, onBack }) {
  const [phoneType, setPhoneType] = useState('android');
  const [forwarding, setForwarding] = useState('unanswered');
  const [copied, setCopied] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testDone, setTestDone] = useState(false);
  const [testError, setTestError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [carrier, setCarrier] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  // Use dynamically provisioned number from onboarding data
  const virtualNumber = data.virtualNumber || data.virtual_number || '+15078814215';

  // Auto-detect carrier on mount
  useEffect(() => {
    if (data.phone && virtualNumber) {
      detectCarrier(data.phone);
    }
  }, []);

  const detectCarrier = async (phone) => {
    setLookingUp(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/carrier-lookup?phone=${encodeURIComponent(phone)}`);
      const result = await res.json();
      if (result.success) {
        setCarrier(result);
      }
    } catch (e) {}
    setLookingUp(false);
  };

  // Generate forwarding code — carrier-specific when available
  const getCode = (fwdType) => {
    const cleaned = virtualNumber.replace('+', '').replace(/\s/g, '');
    const dial = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned : `+${cleaned}`;

    // Use carrier-detected code for unanswered (primary use case)
    if (carrier && fwdType === 'unanswered') {
      return carrier.forward_code;
    }

    return FORWARDING_CODES[phoneType][fwdType]?.replace('{NUMBER}', dial) || `*61*${dial}*11*10#`;
  };

  const code = getCode(forwarding);

  useEffect(() => {
    updateData({ forwardingCode: code });
  }, [code]);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const sendToWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hi! I need to set up call forwarding.\n\nPlease dial this code on my phone:\n${code}\n\nThis activates Aizaan call capture.`
    );
    window.open(`https://wa.me/?text=${msg}`);
  };

  const runTest = async () => {
    setTesting(true);
    setTestError('');
    try {
      const token = localStorage.getItem('cia_token') || localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/calls/test-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await res.json();
      if (result.success) {
        setTestDone(true);
        updateData({ testCallDone: true });
      } else {
        setTestError(result.error || 'Test call failed. Please try again.');
      }
    } catch (e) {
      setTestError('Connection failed. Please try again.');
    }
    setTesting(false);
  };

  return (
    <div className="ob-card">
      <h1 className="ob-title">You are almost live!</h1>
      <p className="ob-subtitle">
        Your dedicated number is ready. One quick step to activate it.
      </p>

      {/* Virtual Number */}
      <div className="ob-number-display">
        <div className="ob-number-label">Your dedicated business number</div>
        <div className="ob-number-value">{virtualNumber}</div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Your existing number stays unchanged. This works behind the scenes.
        </p>
      </div>

      {/* Phone type selector */}
      <div style={{ marginBottom: 16 }}>
        <label className="ob-label">What type of phone do you use?</label>
        <div className="ob-tabs">
          {PHONE_TYPES.map(t => (
            <button
              key={t.key}
              className={`ob-tab ${phoneType === t.key ? 'active' : ''}`}
              onClick={() => setPhoneType(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Carrier detection banner */}
      {lookingUp && (
        <div style={{ padding: '10px 14px', background: 'var(--bg-primary)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          🔍 Detecting your carrier to generate the correct forwarding code...
        </div>
      )}
      {carrier && !lookingUp && (
        <div style={{
          padding: '10px 14px', background: 'rgba(0,200,150,0.08)',
          border: '1px solid rgba(0,200,150,0.2)',
          borderRadius: 8, marginBottom: 16, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ color: 'var(--green)' }}>✓</span>
          <span>Carrier detected: <strong style={{ color: 'var(--text-primary)' }}>{carrier.carrier}</strong> — forwarding code generated automatically</span>
        </div>
      )}

      {/* Forwarding type */}
      <div style={{ marginBottom: 20 }}>
        <label className="ob-label">When should calls be forwarded?</label>
        <div className="ob-tabs">
          <button className={`ob-tab ${forwarding === 'unanswered' ? 'active' : ''}`} onClick={() => setForwarding('unanswered')}>
            When unanswered ⭐
          </button>
          <button className={`ob-tab ${forwarding === 'all' ? 'active' : ''}`} onClick={() => setForwarding('all')}>
            All calls
          </button>
          <button className={`ob-tab ${forwarding === 'busy' ? 'active' : ''}`} onClick={() => setForwarding('busy')}>
            When busy
          </button>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          {forwarding === 'unanswered' && '⭐ Recommended — your phone rings first (10 seconds). We only capture missed calls.'}
          {forwarding === 'all' && '⚠️ Every call goes through Aizaan. US carrier numbers work best with this setting. If you experience issues, switch to "When unanswered".'}
          {forwarding === 'busy' && 'Only captures calls when you are already on another call.'}
        </p>
      </div>

      {/* Forwarding code */}
      <div style={{ marginBottom: 20 }}>
        <label className="ob-label">Step 1 — Dial this code on your phone now:</label>
        <div className="ob-code-display">
          <div className="ob-code-label">Dial exactly this:</div>
          <div className="ob-code-value">{code}</div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <a
            href={`tel:${code}`}
            className="ob-btn ob-btn-primary"
            style={{ flex: 1, fontSize: 13, textDecoration: 'none', textAlign: 'center', padding: '12px' }}
          >
            📞 Dial now
          </a>
          <button
            className="ob-btn ob-btn-ghost"
            onClick={copyCode}
            style={{ flex: 1, fontSize: 13 }}
          >
            {copied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy code</>}
          </button>
          <button
            className="ob-btn ob-btn-ghost"
            onClick={sendToWhatsApp}
            style={{ flex: 1, fontSize: 13 }}
          >
            💬 Send to WhatsApp
          </button>
        </div>

        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{ background: 'none', border: 'none', color: 'var(--blue-light)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <HelpCircle size={13} /> Having trouble? See carrier-specific instructions
        </button>

        {showHelp && (
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 16,
            marginTop: 12,
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
          }}>
            <p><strong style={{ color: 'var(--text-primary)' }}>Jio (India):</strong> Settings → Call Settings → Call Forwarding → When Unanswered → Enter number manually</p>
            <p style={{ marginTop: 8 }}><strong style={{ color: 'var(--text-primary)' }}>Airtel (India):</strong> Dial *61*{virtualNumber}# directly from dialpad</p>
            <p style={{ marginTop: 8 }}><strong style={{ color: 'var(--text-primary)' }}>T-Mobile / Tello (US):</strong> Dial the code exactly as shown above from your dialpad</p>
            <p style={{ marginTop: 8 }}><strong style={{ color: 'var(--text-primary)' }}>AT&T / Verizon (US):</strong> Dial *61*{virtualNumber}# from your dialpad</p>
            <p style={{ marginTop: 8 }}><strong style={{ color: 'var(--text-primary)' }}>Etisalat / du (UAE):</strong> Dial *21*{virtualNumber}# for all calls</p>
            <p style={{ marginTop: 8 }}><strong style={{ color: 'var(--text-primary)' }}>Need help?</strong> Email us at support@aizaan.app and we will set it up for you.</p>
          </div>
        )}
      </div>

      {/* Test call */}
      <div style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 16,
        marginBottom: 20,
      }}>
        <label className="ob-label" style={{ marginBottom: 8 }}>
          Step 2 — Test your setup
        </label>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
          After dialling the code — click below. We will call your number to confirm everything is working.
        </p>

        {testError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px', borderRadius: 6, marginBottom: 12,
            background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 13,
          }}>
            <XCircle size={14} />
            {testError}
          </div>
        )}

        {!testDone ? (
          <button
            className="ob-btn ob-btn-primary"
            onClick={runTest}
            disabled={testing}
            style={{ fontSize: 13 }}
          >
            {testing ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                <span className="ob-loading-dot" />
                <span className="ob-loading-dot" />
                <span className="ob-loading-dot" />
                Calling your number...
              </span>
            ) : (
              <><Phone size={14} /> Test my setup</>
            )}
          </button>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            color: 'var(--green)', fontSize: 14, fontWeight: 600,
          }}>
            <CheckCircle size={20} />
            Setup confirmed! Your number is live.
          </div>
        )}
      </div>

      <button
        className="ob-btn ob-btn-primary"
        onClick={onNext}
        style={{ background: testDone ? 'var(--green)' : 'var(--blue)' }}
      >
        {testDone ? 'Go to my dashboard →' : 'Skip test — go to dashboard →'}
      </button>

      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', display: 'block', margin: '12px auto 0' }}
      >
        ← Back
      </button>
    </div>
  );
}
